import {
  Box,
  Flex,
  Image,
  Stack,
  Text,
  VStack,
  Grid,
} from "@chakra-ui/react";
import React from "react";
import { studentPalette } from "../../../constants/studentPalette";
import { studentSurfaces } from "../../../constants/studentThemeSurfaces";
import { FeedbackQuestionCard } from "../../admin/correct-student-exams/exam-page-feedback";

const ACADEMIC = {
  primary: studentPalette.primary,
  accent: studentPalette.secondary,
  border: studentSurfaces.border,
  cream: studentPalette.cream,
  text: studentSurfaces.text,
  correct: "#276749",
  correctBg: "rgba(39, 103, 73, 0.08)",
  correctBgDark: "rgba(72, 187, 120, 0.12)",
  wrong: "#9B2C2C",
  wrongBg: "rgba(155, 44, 44, 0.08)",
  wrongBgDark: "rgba(245, 101, 101, 0.12)",
};

const darkTextSx = { "html[data-theme='dark'] &": { color: "var(--text-primary)" } };
const darkMutedLabelSx = { "html[data-theme='dark'] &": { color: "var(--text-muted)" } };
const darkQuestionHtmlSx = {
  "html[data-theme='dark'] &": { color: "var(--text-primary)" },
  "html[data-theme='dark'] & p, html[data-theme='dark'] & span, html[data-theme='dark'] & div, html[data-theme='dark'] & li":
    { color: "inherit" },
};
const darkChoiceSx = {
  "html[data-theme='dark'] &": {
    bg: "var(--surface-muted)",
    borderColor: "var(--border-subtle)",
  },
};

const ExamSolvedQuestions = ({
  data,
  answers,
  selectedQuestionIdx,
  correctionArr,
  design = "default",
  questionSerifFont,
}) => {
  const baseUrl = "https://edutopiaonline.com/api";
  const isAcademic = design === "academic";
  const qFont = questionSerifFont || "inherit";

  const filteredAnswers = correctionArr?.filter(
    (val) => val?.question_id == data?.id
  );
  const answersTypes = {
    wrong: { msg: isAcademic ? "Your answer" : "Your Answer" },
    correct: { msg: isAcademic ? "Correct answer" : "Correct Answer" },
    normal: "transparent",
  };

  const handelAnswer = (val) => {
    if (
      filteredAnswers[0]?.student_answer_id == val?.id &&
      val?.right_answer == 1
    )
      return "correct";
    else if (
      filteredAnswers[0]?.student_answer_id == val?.id &&
      val?.right_answer == 0
    )
      return "wrong";
    else if (val?.right_answer == 1) return "correct";
    else return "normal";
  };

  const correction = filteredAnswers?.[0];
  const awarded = Number(correction?.mark ?? correction?.marks_awarded);
  const max = Number(data?.mark ?? correction?.max_marks);
  const feedbackText = String(correction?.comment ?? correction?.feedback ?? "").trim();
  const correctAnswer = correction?.correct_answer ?? data?.correct_answer;
  const isWrittenQuestion = !data?.answers?.length;
  const hasWrittenFeedback = Boolean(
    correction &&
      (feedbackText ||
        correctAnswer ||
        correction.criteria_checks ||
        (isWrittenQuestion && correction.mark != null && correction.mark !== ""))
  );
  const feedbackQuestion = hasWrittenFeedback
    ? {
        id: `Q${(selectedQuestionIdx ?? 0) + 1}`,
        marks_awarded: Number.isFinite(awarded) ? awarded : correction.mark,
        max_marks: Number.isFinite(max) ? max : data?.mark,
        status: correction.status,
        feedback: feedbackText,
        correct_answer: correctAnswer,
        criteria_checks: correction.criteria_checks,
      }
    : null;

  const labelStyles = isAcademic
    ? {
        fontSize: "10px",
        fontWeight: "800",
        color: ACADEMIC.accent,
        textTransform: "uppercase",
        letterSpacing: "0.14em",
        mb: 2,
        sx: darkMutedLabelSx,
      }
    : {
        fontSize: "18px",
        fontWeight: "700",
        textDecoration: "underline",
        mb: 2,
        color: "gray.800",
      };

  return (
    <VStack w="100%" spacing={isAcademic ? "24px" : "40px"}>
      <Box
        w="100%"
        bg={isAcademic ? studentSurfaces.raised : "white"}
        border="1px solid"
        borderColor={isAcademic ? ACADEMIC.border : "gray.300"}
        borderRadius={isAcademic ? "16px" : "12px"}
        p={isAcademic ? { base: 6, md: 10 } : 6}
        boxShadow={isAcademic ? "sm" : "0 2px 8px rgba(0,0,0,0.08)"}
        position="relative"
        overflow="hidden"
      >
        {isAcademic ? (
          <Box
            position="absolute"
            left={0}
            top={0}
            bottom={0}
            w="8px"
            bg={ACADEMIC.primary}
            borderLeftRadius="16px"
          />
        ) : null}
        <Stack
          flexDirection={{ base: "column", lg: "row" }}
          spacing={{ base: 4, lg: 6 }}
          alignItems="flex-start"
        >
          <VStack
            w={{
              base: "100%",
              lg: data?.image && data?.image != "null" ? "60%" : "100%",
            }}
            alignItems="flex-start"
            spacing={4}
            pl={isAcademic ? { base: 2, md: 4 } : 0}
          >
            {data?.comprehension && (
              <Box w="100%">
                <Text {...labelStyles}>Comprehension</Text>
                <Text
                  fontSize={isAcademic ? "md" : "16px"}
                  color={isAcademic ? ACADEMIC.text : "gray.700"}
                  lineHeight="1.65"
                  whiteSpace="pre-wrap"
                  sx={isAcademic ? darkTextSx : undefined}
                >
                  {data?.comprehension}
                </Text>
              </Box>
            )}
            <Box w="100%">
              <Text {...labelStyles}>Question</Text>
              <Box
                fontSize={isAcademic ? { base: "xl", md: "2xl" } : "16px"}
                color={isAcademic ? ACADEMIC.text : "gray.800"}
                lineHeight="1.65"
                fontFamily={isAcademic ? qFont : "inherit"}
                fontWeight={isAcademic ? 400 : undefined}
                sx={{
                  "& p": {
                    margin: 0,
                  },
                  "& *": {
                    maxWidth: "100%",
                  },
                  ...(isAcademic ? darkQuestionHtmlSx : {}),
                }}
              >
                <div dangerouslySetInnerHTML={{ __html: data?.question }} />
              </Box>
            </Box>
          </VStack>

          {data?.image && data?.image != "null" && (
            <Box flex={1} display="flex" justifyContent="center" alignItems="flex-start">
              <Image
                src={`${baseUrl}/${data?.image}`}
                w={{ base: "100%", lg: "auto" }}
                maxH="400px"
                objectFit="contain"
                borderRadius={isAcademic ? "12px" : "8px"}
                borderWidth={isAcademic ? "1px" : 0}
                borderColor={isAcademic ? ACADEMIC.border : undefined}
              />
            </Box>
          )}
        </Stack>
      </Box>

      {data?.answers?.length ? (
      <Box w="100%">
        <Grid
          role="group"
          aria-labelledby="my-radio-group"
          templateColumns={
            isAcademic
              ? "repeat(2, minmax(0, 1fr))"
              : { base: "1fr", lg: "repeat(2, 1fr)" }
          }
          gap={isAcademic ? 4 : 3}
          w="100%"
        >
          {data?.answers?.map((val, index) => {
            const letter = String.fromCharCode(65 + index);
            const answerType = handelAnswer(val);
            const isCorrect = val?.right_answer == 1;
            const isStudentAnswer = filteredAnswers[0]?.student_answer_id == val?.id;

            let borderColor = isAcademic ? ACADEMIC.border : "gray.300";
            let bgColor = isAcademic ? studentSurfaces.raised : "white";
            let letterBg = isAcademic ? studentSurfaces.muted : "gray.200";
            let letterColor = isAcademic ? ACADEMIC.primary : "gray.700";
            let choiceSx = isAcademic && answerType === "normal" ? darkChoiceSx : undefined;

            if (answerType === "correct") {
              borderColor = ACADEMIC.correct;
              bgColor = isStudentAnswer
                ? ACADEMIC.correctBg
                : isAcademic
                  ? studentSurfaces.raised
                  : "green.50";
              letterBg = ACADEMIC.correct;
              letterColor = "white";
              choiceSx = {
                "html[data-theme='dark'] &": {
                  bg: ACADEMIC.correctBgDark,
                  borderColor: ACADEMIC.correct,
                },
              };
            } else if (answerType === "wrong") {
              borderColor = ACADEMIC.wrong;
              bgColor = isAcademic ? ACADEMIC.wrongBg : "red.50";
              letterBg = ACADEMIC.wrong;
              letterColor = "white";
              choiceSx = {
                "html[data-theme='dark'] &": {
                  bg: ACADEMIC.wrongBgDark,
                  borderColor: ACADEMIC.wrong,
                },
              };
            } else if (isCorrect) {
              borderColor = ACADEMIC.correct;
              bgColor = isAcademic ? studentSurfaces.raised : "white";
              letterBg = ACADEMIC.correct;
              letterColor = "white";
              choiceSx = {
                "html[data-theme='dark'] &": {
                  bg: ACADEMIC.correctBgDark,
                  borderColor: ACADEMIC.correct,
                },
              };
            }

            const badgeColor =
              answerType === "correct"
                ? isAcademic
                  ? ACADEMIC.correct
                  : "green.600"
                : answerType === "wrong"
                ? isAcademic
                  ? ACADEMIC.wrong
                  : "red.600"
                : undefined;

            return (
              <Box key={val?.id} w="100%">
                <Flex
                  w="100%"
                  alignItems="center"
                  p={isAcademic ? 6 : 4}
                  borderRadius={isAcademic ? "12px" : "12px"}
                  border="2px solid"
                  borderColor={borderColor}
                  bg={bgColor}
                  boxShadow={
                    isAcademic
                      ? answerType !== "normal"
                        ? "0 4px 14px rgba(91, 13, 22, 0.08)"
                        : "none"
                      : "0 2px 4px rgba(0,0,0,0.1)"
                  }
                  transition="box-shadow 0.2s, border-color 0.2s"
                  sx={choiceSx}
                >
                  <Box
                    w="40px"
                    h="40px"
                    borderRadius="50%"
                    border="2px solid"
                    borderColor={borderColor}
                    bg={letterBg}
                    color={letterColor}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    fontWeight="800"
                    fontSize="18px"
                    mr={isAcademic ? 4 : 3}
                    flexShrink={0}
                  >
                    {letter}
                  </Box>
                  <Text
                    fontSize={isAcademic ? "lg" : "18px"}
                    flex={1}
                    lineHeight="1.5"
                    color={isAcademic ? ACADEMIC.text : "inherit"}
                    fontWeight={isAcademic ? 500 : undefined}
                    sx={isAcademic ? darkTextSx : undefined}
                  >
                    {val?.answer}
                  </Text>
                  {answersTypes[answerType]?.msg && (
                    <Text
                      fontSize="xs"
                      fontWeight="800"
                      color={badgeColor}
                      ml={2}
                      flexShrink={0}
                      textTransform="uppercase"
                      letterSpacing="0.06em"
                      sx={
                        isAcademic
                          ? {
                              "html[data-theme='dark'] &": {
                                color:
                                  answerType === "correct"
                                    ? "#68D391"
                                    : answerType === "wrong"
                                      ? "#FC8181"
                                      : "var(--text-muted)",
                              },
                            }
                          : undefined
                      }
                    >
                      [{answersTypes[answerType]?.msg}]
                    </Text>
                  )}
                </Flex>
              </Box>
            );
          })}
        </Grid>
      </Box>
      ) : null}

      {feedbackQuestion ? (
        <Box
          w="100%"
          borderRadius={isAcademic ? "16px" : "12px"}
          overflow="hidden"
          border="1px solid"
          borderColor={isAcademic ? ACADEMIC.border : "gray.200"}
          boxShadow={isAcademic ? "sm" : "0 2px 8px rgba(0,0,0,0.08)"}
          bg={isAcademic ? studentSurfaces.raised : "white"}
        >
          <FeedbackQuestionCard question={feedbackQuestion} />
        </Box>
      ) : null}
    </VStack>
  );
};

export default ExamSolvedQuestions;
